import { useEffect, useState } from "react"
import { Col, Flex, Row, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { isGuideSelector, powerTranzSettingsAccountSelector } from "store/user"
import { getPowerTranzAccountStatus } from "store/payment-provider"
import { ACCOUNT_STATUSES } from "constants/powertranz"
import { SettingsTitle } from "../../SettingsTitle"
import OnboardingChecklist from "./OnboardingChecklist"
import PowerTranzAccountCard from "./PowerTranzAccountCard"
import PlatformFeeStatements from "./PlatformFeeStatements"
import CardFeeForm from "./CardFeeForm"

const PowerTranzAccount = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // From the account endpoints, not GET /user (cached by the API); never connected = empty account.
  const account = useSelector(powerTranzSettingsAccountSelector)
  // Connect, verify, disconnect and fee statements are the guide owner's alone (the API
  // enforces authorize_guide_owner! too); staff with settings access see the status read-only.
  const isOwner = useSelector(isGuideSelector)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const isCardFeeVisible = isOwner && ACCOUNT_STATUSES.includes(account.connectionStatus)

  useEffect(() => {
    setIsRefreshing(true)
    dispatch(getPowerTranzAccountStatus())
      .catch(() => {})
      .finally(() => setIsRefreshing(false))
  }, [dispatch])

  return (
    <Flex vertical gap={spacings.L}>
      <SettingsTitle title={t("powertranz.settings.title")} />
      <Text>{t("powertranz.settings.description")}</Text>
      {/* The checklist is for getting set up; once credentials are saved it's just noise. */}
      {isOwner && account.isEligible && !ACCOUNT_STATUSES.includes(account.connectionStatus) && <OnboardingChecklist />}
      {/* The fee sits beside the account on wide screens and below it on narrow ones. */}
      <Row gutter={[24, 24]} align="top">
        <Col span={24} xl={isCardFeeVisible ? 15 : 24}>
          <Spin spinning={isRefreshing} tip={t("powertranz.settings.refreshing")}>
            <PowerTranzAccountCard account={account} isOwner={isOwner} />
          </Spin>
        </Col>
        {isCardFeeVisible && (
          <Col span={24} xl={9}>
            <CardFeeForm cardFeeRate={account.cardFeeRate} />
          </Col>
        )}
      </Row>
      {/* Statements stay available after a disconnect: past PowerTranz payments still carry fees. */}
      {isOwner && account.connectionStatus && <PlatformFeeStatements />}
    </Flex>
  )
}

export default PowerTranzAccount
